export type CurrentUser = {
  githubLogin: string;
  displayName: string;
  avatarUrl: string | null;
};

export type SessionState = {
  authenticated: boolean;
  user: CurrentUser | null;
  csrfToken: string;
};

export type HealthState = {
  ok: boolean;
  service: string;
  db: {
    ok: boolean;
    detail: string;
  };
  timestamp: string;
};

export type ContactMessageDto = {
  id: string;
  name: string;
  email: string;
  company: string | null;
  message: string;
  status: "new" | "reviewed" | "resolved";
  source: string;
  createdAt: string;
  updatedAt: string;
};

export type SiteSettings = {
  statusHeadline: string;
  availability: string;
  stackTags: string[];
  updatedAt?: string;
};

const signedOutSession: SessionState = {
  authenticated: false,
  user: null,
  csrfToken: "",
};

async function parseJson<T>(response: Response): Promise<T> {
  const body = (await response.json()) as T & { error?: string };
  if (!response.ok) {
    throw new Error(body.error ?? `Request failed with ${response.status}`);
  }
  return body;
}

export async function getMe(): Promise<SessionState> {
  const response = await fetch("/api/me", { credentials: "include" });
  if (!response.headers.get("content-type")?.includes("application/json")) {
    return signedOutSession;
  }
  return parseJson<SessionState>(response);
}

export async function getHealth(): Promise<HealthState> {
  return parseJson<HealthState>(await fetch("/api/health", { credentials: "include" }));
}

export async function sendContact(
  csrfToken: string,
  payload: {
    name: string;
    email: string;
    company: string;
    message: string;
  },
): Promise<{ id: string; status: string; createdAt: string }> {
  return parseJson(
    await fetch("/api/contact", {
      method: "POST",
      credentials: "include",
      headers: {
        "content-type": "application/json",
        "x-csrf-token": csrfToken,
      },
      body: JSON.stringify(payload),
    }),
  );
}

export async function logout(csrfToken: string): Promise<void> {
  await parseJson(
    await fetch("/api/auth/logout", {
      method: "POST",
      credentials: "include",
      headers: {
        "x-csrf-token": csrfToken,
      },
    }),
  );
}

export async function getAdminContacts(): Promise<ContactMessageDto[]> {
  const result = await parseJson<{ messages: ContactMessageDto[] }>(
    await fetch("/api/admin/contact-messages", { credentials: "include" }),
  );
  return result.messages;
}

export async function updateContactStatus(
  csrfToken: string,
  id: string,
  status: ContactMessageDto["status"],
): Promise<ContactMessageDto> {
  const result = await parseJson<{ message: ContactMessageDto }>(
    await fetch(`/api/admin/contact-messages/${id}`, {
      method: "PATCH",
      credentials: "include",
      headers: {
        "content-type": "application/json",
        "x-csrf-token": csrfToken,
      },
      body: JSON.stringify({ status }),
    }),
  );
  return result.message;
}

export async function getSiteSettings(): Promise<SiteSettings> {
  const result = await parseJson<{ settings: SiteSettings }>(
    await fetch("/api/admin/site-settings", { credentials: "include" }),
  );
  return result.settings;
}

export async function updateSiteSettings(csrfToken: string, settings: SiteSettings): Promise<SiteSettings> {
  const result = await parseJson<{ settings: SiteSettings }>(
    await fetch("/api/admin/site-settings", {
      method: "PATCH",
      credentials: "include",
      headers: {
        "content-type": "application/json",
        "x-csrf-token": csrfToken,
      },
      body: JSON.stringify(settings),
    }),
  );
  return result.settings;
}
